import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Chip,
    IconButton,
    TextField,
    InputAdornment,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

import {
    getBusinesses,
    getBranches,
    createBranch,
    updateBranch,
    deleteBranch,
    getContacts,
} from '../api';
import { useAuth } from '../context/AuthContext';
import BranchForm from '../components/BranchForm';
import BranchList from '../components/BranchList';
import { useNotification } from '../context/NotificationContext';
import { useSelection } from '../context/SelectionContext';
import { dedupeBusinesses, deriveBusinessDisplayName } from '../utils/business';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from '@mui/material/Tooltip';
import { useLocation, useNavigate } from 'react-router-dom';

let branchesPageCache = null;

const BranchesPage = () => {
    const { isAdmin, userRole, userBusinessId, userContactId, user } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();
    const userId = user?.uid || 'anonymous';
    const cachedState = branchesPageCache && branchesPageCache.userId === userId ? branchesPageCache : null;
    const cacheMatchesContext = Boolean(
        cachedState && cachedState.isAdmin === isAdmin && cachedState.userBusinessId === userBusinessId,
    );

    const [businesses, setBusinesses] = useState(cachedState?.businesses || []);
    const [branches, setBranches] = useState(cachedState?.branches || []);
    const [branchesBusinessId, setBranchesBusinessId] = useState(cachedState?.branchesBusinessId || '');
    const [contacts, setContacts] = useState(cachedState?.contacts || []);
    const [contactsBusinessId, setContactsBusinessId] = useState(cachedState?.contactsBusinessId || '');
    const [loading, setLoading] = useState(
        cachedState ? { businesses: false, branches: false, contacts: false } : { businesses: true, branches: false, contacts: false },
    );
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingBranch, setEditingBranch] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, branch: null });
    const { showNotification } = useNotification();
    const {
        selectedBusinessId,
        selectedBusinessName,
        selectedBranchId,
        setSelectedBusinessId,
        clearBranch,
        setSelectedBranchId,
    } = useSelection();
    const [searchQuery, setSearchQuery] = useState(cachedState?.searchQuery || '');
    const [branchFilterIds, setBranchFilterIds] = useState(cachedState?.branchFilterIds || []);
    const [branchFilterLabel, setBranchFilterLabel] = useState(cachedState?.branchFilterLabel || '');

    const canCreateBranch = isAdmin || userRole === 'Business Owner';

    const loadBusinesses = async () => {
        const skipLoading = cacheMatchesContext && businesses.length;
        if (!skipLoading) {
            setLoading(prev => ({ ...prev, businesses: true }));
        }
        try {
            const response = await getBusinesses();
            const fetched = (response.data || []).filter(b => (b.status || 'ACTIVE') !== 'INACTIVE');
            const uniqueBusinesses = dedupeBusinesses(fetched);
            setBusinesses(uniqueBusinesses);
            if (selectedBusinessId && !selectedBusinessName) {
                const matched = uniqueBusinesses.find(b => b.businessId === selectedBusinessId);
                if (matched) {
                    setSelectedBusinessId(matched.businessId, deriveBusinessDisplayName(matched));
                }
            }
            if (!isAdmin && userBusinessId) {
                const matchingBusiness = uniqueBusinesses.find(b => b.businessId === userBusinessId);
                if (matchingBusiness) {
                    setSelectedBusinessId(matchingBusiness.businessId, deriveBusinessDisplayName(matchingBusiness));
                } else {
                    setSelectedBusinessId('');
                }
            } else if (uniqueBusinesses.length > 0) {
                const exists = uniqueBusinesses.some(b => b.businessId === selectedBusinessId);
                if (!selectedBusinessId || !exists) {
                    const fallbackBusiness = uniqueBusinesses[0];
                    setSelectedBusinessId(fallbackBusiness.businessId, deriveBusinessDisplayName(fallbackBusiness));
                }
            } else {
                setSelectedBusinessId('');
            }
        } catch (err) {
            console.error('Failed to load businesses for branches:', err);
            setError('Could not load businesses. Please refresh the page.');
        } finally {
            setLoading(prev => ({ ...prev, businesses: false }));
        }
    };

    useEffect(() => {
        loadBusinesses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, userBusinessId]);

    const loadBranches = async (businessId, { force = false } = {}) => {
        if (!businessId) {
            setBranches([]);
            setBranchesBusinessId('');
            setContacts([]);
            setContactsBusinessId('');
            setBranchFilterIds([]);
            setBranchFilterLabel('');
            setLoading(prev => ({ ...prev, branches: false, contacts: false }));
            return;
        }
        const skipLoading = !force && cacheMatchesContext && branchesBusinessId === businessId && branches.length;
        if (!skipLoading) {
            setLoading(prev => ({ ...prev, branches: true }));
        }
        try {
            const response = await getBranches(businessId);
            const activeBranches = (response.data || []).filter(branch => (branch.status || 'ACTIVE') !== 'INACTIVE');
            setBranches(activeBranches);
            setBranchesBusinessId(businessId);
        } catch (err) {
            console.error('Failed to load branches:', err);
            setError('Could not load branches for the selected business.');
        } finally {
            setLoading(prev => ({ ...prev, branches: false }));
        }
    };

    const loadContacts = async (businessId, { force = false } = {}) => {
        if (!businessId) {
            setContacts([]);
            setContactsBusinessId('');
            setLoading(prev => ({ ...prev, contacts: false }));
            return;
        }
        const skipLoading = !force && cacheMatchesContext && contactsBusinessId === businessId && contacts.length;
        if (!skipLoading) {
            setLoading(prev => ({ ...prev, contacts: true }));
        }
        try {
            const response = await getContacts(businessId);
            setContacts(response.data || []);
            setContactsBusinessId(businessId);
        } catch (err) {
            console.error('Failed to fetch contacts for branches:', err);
        } finally {
            setLoading(prev => ({ ...prev, contacts: false }));
        }
    };

    useEffect(() => {
        if (!user) {
            branchesPageCache = null;
        }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        branchesPageCache = {
            userId,
            isAdmin,
            userBusinessId,
            businesses,
            branches,
            branchesBusinessId,
            contacts,
            contactsBusinessId,
            selectedBusinessId,
            selectedBranchId,
            searchQuery,
            branchFilterIds,
            branchFilterLabel,
            timestamp: Date.now(),
        };
    }, [
        user,
        userId,
        isAdmin,
        userBusinessId,
        businesses,
        branches,
        branchesBusinessId,
        contacts,
        contactsBusinessId,
        selectedBusinessId,
        selectedBranchId,
        searchQuery,
        branchFilterIds,
        branchFilterLabel,
    ]);

    useEffect(() => {
        if (selectedBusinessId) {
            loadBranches(selectedBusinessId);
            loadContacts(selectedBusinessId);
        } else {
            setBranches([]);
            setBranchesBusinessId('');
            setContacts([]);
            setContactsBusinessId('');
            setBranchFilterIds([]);
            setBranchFilterLabel('');
        }
        setSearchQuery('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBusinessId]);

    useEffect(() => {
        if (!location.state) return;
        const {
            businessId,
            businessName,
            branchId,
            branchIds,
            branchFilterLabel: incomingLabel,
            searchQuery: incomingSearch,
        } = location.state;
        if (businessId && businessId !== selectedBusinessId) {
            setSelectedBusinessId(businessId, businessName || resolveBusinessName(businessId));
        }
        if (Array.isArray(branchIds)) {
            if (branchIds.length) {
                setBranchFilterIds(branchIds);
                setBranchFilterLabel(incomingLabel || 'Filtered branches');
            } else {
                setBranchFilterIds([]);
                setBranchFilterLabel('');
            }
        } else if (branchId) {
            setBranchFilterIds([]);
            setBranchFilterLabel('');
        }
        if (branchId) {
            setSelectedBranchId(branchId);
        }
        if (typeof incomingSearch === 'string') {
            setSearchQuery(incomingSearch);
        }
        navigate(location.pathname, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const handleOpenCreateDialog = () => {
        setEditingBranch(null);
        setDialogOpen(true);
    };

    const handleViewDocuments = (branch) => {
        if (!branch?.branchId) return;
        if (branch.businessId && branch.businessId !== selectedBusinessId) {
            setSelectedBusinessId(branch.businessId, resolveBusinessName(branch.businessId));
        }
        setSelectedBranchId(branch.branchId);
        navigate('/documents');
    };

    const handleOpenUpdateDialog = (branch) => {
        setEditingBranch(branch);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setEditingBranch(null);
        setDialogOpen(false);
    };

    const handleViewContacts = (branch) => {
        if (!branch?.branchId) return;
        if (branch.businessId && branch.businessId !== selectedBusinessId) {
            setSelectedBusinessId(branch.businessId, resolveBusinessName(branch.businessId));
        }
        setSelectedBranchId(branch.branchId);
        navigate('/contacts', {
            state: {
                branchId: branch.branchId,
                branchLabel: branch.nickname || branch.address || 'Selected Branch',
                businessId: branch.businessId || selectedBusinessId,
                businessName: resolveBusinessName(branch.businessId || selectedBusinessId),
            },
        });
    };

    const handleFormSubmit = async (formValues, branchId) => {
        try {
            const payload = {
                ...formValues,
                businessId: formValues.businessId || selectedBusinessId,
            };

            if (!payload.businessId) {
                showNotification({ message: 'Please select a business before saving the branch.', severity: 'warning' });
                return;
            }

            if (branchId) {
                await updateBranch(branchId, payload);
                showNotification({ message: 'Branch updated successfully.', severity: 'success' });
            } else {
                await createBranch(payload);
                showNotification({ message: 'Branch created successfully.', severity: 'success' });
            }

            if (payload.businessId) {
                await Promise.all([
                    loadBranches(payload.businessId, { force: true }),
                    loadContacts(payload.businessId, { force: true }),
                ]);
                setSelectedBusinessId(payload.businessId, resolveBusinessName(payload.businessId));
            }
        } catch (err) {
            console.error('Failed to save branch:', err);
            showNotification({ message: 'Could not save the branch. Please try again.', severity: 'error' });
        } finally {
            handleCloseDialog();
        }
    };

    const handleDeleteClick = (branch) => {
        setDeleteDialog({ open: true, branch });
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialog({ open: false, branch: null });
    };

    const handleConfirmDelete = async () => {
        const branch = deleteDialog.branch;
        if (!branch) return;
        const businessId = branch.businessId || selectedBusinessId;
        if (!businessId) {
            showNotification({ message: 'Cannot determine business for this branch.', severity: 'warning' });
            handleCloseDeleteDialog();
            return;
        }
        try {
            await deleteBranch(branch.branchId, businessId);
            showNotification({ message: 'Branch deleted successfully.', severity: 'success' });
            await Promise.all([
                loadBranches(businessId, { force: true }),
                loadContacts(businessId, { force: true }),
            ]);
        } catch (err) {
            console.error('Failed to delete branch:', err);
            showNotification({ message: 'Could not delete the branch. Please try again.', severity: 'error' });
        } finally {
            handleCloseDeleteDialog();
        }
    };

    const resolveBusinessName = useCallback(
        (businessId) => {
            const match = businesses.find(b => b.businessId === businessId);
            return deriveBusinessDisplayName(match);
        },
        [businesses],
    );

    const effectiveUserContactId = useMemo(() => {
        if (userContactId) return userContactId;
        const match = contacts.find(contact => contact.firebaseUid === user?.uid);
        return match?.contactId || null;
    }, [userContactId, contacts, user]);

    const filteredBranches = useMemo(() => {
        let result = branches;
        if (branchFilterIds.length) {
            const allowed = new Set(branchFilterIds);
            result = result.filter(branch => allowed.has(branch.branchId));
        }
        const term = searchQuery.trim().toLowerCase();
        if (!term) return result;
        return result.filter(branch => {
            const values = [branch.nickname, branch.address, branch.email, branch.phoneNumber];
            return values.some(value => typeof value === 'string' && value.toLowerCase().includes(term));
        });
    }, [branches, branchFilterIds, searchQuery]);

    const handleClearBranchFilter = useCallback(() => {
        setBranchFilterIds([]);
        setBranchFilterLabel('');
    }, []);

    useEffect(() => {
        if (!branchFilterIds.length) return;
        const existing = new Set(branches.map(branch => branch.branchId));
        const filtered = branchFilterIds.filter(id => existing.has(id));
        if (filtered.length !== branchFilterIds.length) {
            setBranchFilterIds(filtered);
            if (!filtered.length) {
                setBranchFilterLabel('');
            }
        }
    }, [branches, branchFilterIds]);

    // const pageLoading = (
    //     (loading.businesses && businesses.length === 0)
    //     || (loading.branches && branches.length === 0)
    //     || (loading.contacts && contacts.length === 0)
    // );

    return (
        <Box>
            {/* <Backdrop
                open={pageLoading}
                sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop> */}
            {/* <Typography variant="h1" gutterBottom>Branches</Typography> */}
            <Stack spacing={3}>
                <Paper elevation={isMobile ? 0 : 3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, bgcolor: isMobile ? 'background.paper' : 'background.default' }}>
                    <Stack spacing={2}>
                        {/* <Typography variant="h4" fontWeight={700}>Branches</Typography> */}
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            sx={{ flexWrap: 'wrap' }}
                        >
                            {isAdmin && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: { xs: '100%', md: 'auto' } }}>
                                    <FormControl sx={{ minWidth: { xs: '100%', md: 260 } }} disabled={loading.businesses}>
                                        <InputLabel id="branch-business-filter">Select Business</InputLabel>
                                        <Select
                                            labelId="branch-business-filter"
                                            label="Select Business"
                                            value={selectedBusinessId}
                                            onChange={(e) => setSelectedBusinessId(e.target.value, resolveBusinessName(e.target.value))}
                                        >
                                            {businesses.map(business => (
                                                <MenuItem key={business.businessId} value={business.businessId}>
                                                    {deriveBusinessDisplayName(business)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Tooltip title="Clear business filter">
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setSelectedBusinessId('');
                                                    clearBranch();
                                                }}
                                                disabled={!selectedBusinessId}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' }, flexGrow: 1 }}>
                                <TextField
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search branches"
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    fullWidth
                                    sx={{ minWidth: { xs: '100%', md: 260 } }}
                                />
                            </Box>
                            {canCreateBranch && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleOpenCreateDialog}
                                    disabled={loading.businesses || !selectedBusinessId}
                                    sx={{ width: { xs: '100%', md: 'auto' } }}
                                >
                                    Add Branch
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                    {branchFilterIds.length > 0 && (
                        <Chip
                            label={branchFilterLabel || `Filtered branches (${branchFilterIds.length})`}
                            onDelete={handleClearBranchFilter}
                            color="primary"
                            variant="outlined"
                            sx={{ alignSelf: 'flex-start' }}
                        />
                    )}
                </Paper>

                {error && <Alert severity="error">{error}</Alert>}

                {loading.branches ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <BranchList
                        branches={filteredBranches}
                        contacts={contacts}
                        onUpdateClick={handleOpenUpdateDialog}
                        onDeleteClick={handleDeleteClick}
                        onViewDocuments={handleViewDocuments}
                        onViewContacts={handleViewContacts}
                        isAdmin={isAdmin}
                        userRole={userRole}
                        userContactId={effectiveUserContactId}
                    />
                )}
            </Stack>

            <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog} aria-labelledby="delete-branch-title">
                <DialogTitle id="delete-branch-title">Delete Branch</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {deleteDialog.branch
                            ? `Are you sure you want to delete the branch "${deleteDialog.branch.nickname || deleteDialog.branch.address || deleteDialog.branch.branchId}"? This action cannot be undone.`
                            : 'Are you sure you want to delete this branch?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle>
                    {editingBranch ? 'Edit Branch' : 'Create Branch'}
                    <IconButton onClick={handleCloseDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <BranchForm
                        onSubmit={handleFormSubmit}
                        onCancel={handleCloseDialog}
                        businesses={businesses}
                        contacts={contacts}
                        initialData={editingBranch}
                        defaultBusinessId={selectedBusinessId}
                        disableBusinessSelection={!isAdmin}
                        disableSupervisorSelection={userRole === 'Supervisor'}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default BranchesPage;
